import {
	DocumentFormat,
	DocumentFormatValues,
	DocumentGenerationConfig,
	DocumentTemplate,
	GENERATE_DOCUMENT_TYPE_CONFIG,
	ProjectDocumentGenerationTypeKeys,
	type PropositionProductTypeValues,
} from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Label,
	RadioGroup,
	RadioGroupItem,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import { useQueryClient } from "@tanstack/react-query";
import { FileCode, FileText } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useGenerateMultipleDocuments } from "../../../../api/mutations/propositions.mutations";
import { useGenerateQuittanceWithReceiptId } from "../../../../api/mutations/receipts.mutations";
import { useGetDocumentTemplates } from "../../../../api/queries/documents.queries";
import { useProposition } from "../../../../api/queries/propositions.queries";
import {
	useGetDuneSignatories,
	useMe,
} from "../../../../api/queries/users.queries";
import { useReceiptsByPropositionQuery } from "../../../../api/queries/receipt.queries";
import { propositionsQueryKeys } from "../../../../api/query-keys/propositions.query-keys";
import { tasksQueryKeys } from "../../../../api/query-keys/tasks.query-keys";

interface GenerateGFADocumentsModalProps {
	types: ProjectDocumentGenerationTypeKeys[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
	propositionId: number;
	onSuccess?: (data?: {
		documentUrl: string;
		documentId: string;
		fileId: string;
		message: string;
	}) => void;
}

const GenerateGFADocumentsModal: React.FC<GenerateGFADocumentsModalProps> = ({
	types,
	open,
	onOpenChange,
	propositionId,
	onSuccess,
}) => {
	const [documentConfigs, setDocumentConfigs] = useState<
		Record<
			ProjectDocumentGenerationTypeKeys,
			{ templateId: string; format: DocumentFormatValues }
		>
	>(() => {
		const initial: Record<
			string,
			{ templateId: string; format: DocumentFormatValues }
		> = {};
		for (const t of types) {
			initial[t] = { templateId: "", format: DocumentFormat.PDF };
		}
		return initial;
	});

	const [selectedSignatoryId, setSelectedSignatoryId] = useState<string>("");
	const [quittanceReceiptId, setQuittanceReceiptId] = useState<string>("");
	const hasQuittanceType = types.includes("QUITTANCE");

	const { data: allTemplates, isLoading: isLoadingTemplates } =
		useGetDocumentTemplates();

	const { data: duneSignatories, isLoading: isLoadingSignatories } =
		useGetDuneSignatories();

	const { data: proposition } = useProposition(propositionId);

	const { data: receipts, isLoading: isLoadingReceipts } =
		useReceiptsByPropositionQuery(propositionId);

	const { me } = useMe();

	const getTemplateTypeForProductType = useCallback(
		(
			docType: ProjectDocumentGenerationTypeKeys,
			productType: PropositionProductTypeValues,
		) => {
			const typeConfig = GENERATE_DOCUMENT_TYPE_CONFIG[docType];
			return typeConfig.getTemplateType?.(productType);
		},
		[],
	);

	const getValidTemplatesForType = useCallback(
		(docType: ProjectDocumentGenerationTypeKeys) => {
			if (!allTemplates) return [];

			if (docType === "QUITTANCE") {
				return allTemplates.filter(
					(template: DocumentTemplate) =>
						template.entityModel === "QUITTANCE" && template.validated,
				);
			}

			if (!proposition?.productType) return [];

			const expectedTemplateType = getTemplateTypeForProductType(
				docType,
				proposition.productType,
			);
			return allTemplates.filter(
				(template: DocumentTemplate) =>
					template.validated && template.templateType === expectedTemplateType,
			);
		},
		[allTemplates, proposition?.productType, getTemplateTypeForProductType],
	);

	const availableReceipts = useMemo(() => {
		return (
			receipts?.map((receipt) => ({
				id: receipt.id.toString(),
				label: receipt.reference,
				value: receipt.id.toString(),
				subtitle: receipt.type,
			})) || []
		);
	}, [receipts]);

	useEffect(() => {
		if (me && duneSignatories && !selectedSignatoryId) {
			const currentUserSignatory = duneSignatories.find(
				(signatory) => signatory.id === me.id,
			);
			if (currentUserSignatory) {
				setSelectedSignatoryId(currentUserSignatory.id.toString());
			}
		}
	}, [me, duneSignatories, selectedSignatoryId]);

	const queryClient = useQueryClient();
	const multiMutation = useGenerateMultipleDocuments(propositionId);
	const generateQuittanceMutation =
		useGenerateQuittanceWithReceiptId(propositionId);

	const handleGenerate = async () => {
		if (!selectedSignatoryId) {
			toast.error("Veuillez sélectionner un signataire Dune");
			return;
		}

		const documentsToGenerate: DocumentGenerationConfig[] = types
			.filter((t) => t !== "QUITTANCE" && documentConfigs[t]?.templateId)
			.map((t) => ({
				type: t,
				templateId: documentConfigs[t].templateId,
				format: documentConfigs[t].format,
			}));

		const hasQuittance =
			hasQuittanceType &&
			documentConfigs.QUITTANCE?.templateId &&
			quittanceReceiptId;

		if (documentsToGenerate.length === 0 && !hasQuittance) {
			toast.error("Veuillez sélectionner au moins un document à générer");
			return;
		}

		const promises: Promise<unknown>[] = [];

		if (documentsToGenerate.length > 0) {
			promises.push(
				multiMutation.mutateAsync({
					documents: documentsToGenerate,
					signataireDuneId: Number(selectedSignatoryId),
					skipNextTasks: true,
					completeGfaGenerationTask: true,
				}),
			);
		}

		if (hasQuittance) {
			promises.push(
				generateQuittanceMutation.mutateAsync({
					receiptId: quittanceReceiptId,
					templateId: documentConfigs.QUITTANCE.templateId,
					signataireDuneId: Number(selectedSignatoryId),
					format: documentConfigs.QUITTANCE.format,
					skipTaskValidation: true,
				}),
			);
		}
		try {
			await Promise.all(promises);
			toast.success("Documents générés avec succès");
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.tasks(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.documents(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.detail(propositionId),
					refetchType: "active",
				}),
				queryClient.invalidateQueries({
					queryKey: tasksQueryKeys.activeTasksList(),
					refetchType: "active",
				}),
			]);
			onOpenChange(false);
			if (onSuccess) {
				onSuccess();
			}
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Erreur lors de la génération";
			toast.error(message);
		}
	};

	const formatOptions = [
		{
			value: DocumentFormat.PDF,
			label: "PDF",
			description: "Pour signature électronique",
			icon: FileText,
		},
		{
			value: DocumentFormat.DOCX,
			label: "Word",
			description: "Pour permettre la modification",
			icon: FileCode,
		},
	];

	const isGenerating =
		multiMutation.isPending || generateQuittanceMutation.isPending;

	const hasAtLeastOneSelection = types.some((t) => {
		if (t === "QUITTANCE") {
			return documentConfigs.QUITTANCE?.templateId && quittanceReceiptId;
		}
		return documentConfigs[t]?.templateId;
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>Générer des documents</DialogTitle>
					<DialogDescription>
						Sélectionnez les modèles, le signataire Dune et les formats de
						documents
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-6 py-4">
					{types
						.filter((t) => t !== "QUITTANCE")
						.map((docType) => {
							const typeConfig = GENERATE_DOCUMENT_TYPE_CONFIG[docType];
							const validTemplates = getValidTemplatesForType(docType);

							return (
								<div key={docType} className="space-y-4 border rounded-lg p-4">
									<h3 className="font-medium text-sm">
										{typeConfig.title || docType}
									</h3>

									<div className="space-y-2">
										<label
											htmlFor={`template-select-${docType}`}
											className="text-sm font-medium"
										>
											{typeConfig.templateLabel || "Modèle"}
										</label>
										<Select
											value={documentConfigs[docType]?.templateId || ""}
											onValueChange={(value) => {
												setDocumentConfigs((prev) => ({
													...prev,
													[docType]: {
														...prev[docType],
														templateId: value,
													},
												}));
											}}
											disabled={
												isLoadingTemplates || validTemplates.length === 0
											}
										>
											<SelectTrigger id={`template-select-${docType}`}>
												<SelectValue
													placeholder={
														isLoadingTemplates
															? "Chargement..."
															: validTemplates.length === 0
																? "Aucun modèle disponible"
																: "Sélectionnez un modèle"
													}
												/>
											</SelectTrigger>
											<SelectContent>
												{validTemplates.map((template: DocumentTemplate) => (
													<SelectItem key={template.id} value={template.id}>
														{template.name} (v{template.version})
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									{documentConfigs[docType]?.templateId && (
										<fieldset className="space-y-2">
											<legend className="text-sm font-medium">
												Format du document
											</legend>
											<RadioGroup
												value={
													documentConfigs[docType]?.format || DocumentFormat.PDF
												}
												onValueChange={(value) => {
													setDocumentConfigs((prev) => ({
														...prev,
														[docType]: {
															...prev[docType],
															format: value as DocumentFormatValues,
														},
													}));
												}}
												className="grid grid-cols-2 gap-3"
											>
												{formatOptions.map((option) => {
													const Icon = option.icon;
													return (
														<Label
															key={`${docType}-${option.value}`}
															htmlFor={`${docType}-${option.value}`}
															className="relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-input p-4 shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
														>
															<RadioGroupItem
																id={`${docType}-${option.value}`}
																value={option.value}
																className="absolute top-2 right-2"
															/>
															<Icon className="h-8 w-8 mb-1" />
															<span className="font-medium text-center">
																{option.label}
															</span>
															<span className="text-xs text-muted-foreground text-center">
																{option.description}
															</span>
														</Label>
													);
												})}
											</RadioGroup>
										</fieldset>
									)}
								</div>
							);
						})}

					{hasQuittanceType &&
						types
							.filter((t) => t === "QUITTANCE")
							.map((docType) => {
								const typeConfig = GENERATE_DOCUMENT_TYPE_CONFIG[docType];
								const validTemplates = getValidTemplatesForType(docType);

								return (
									<div
										key={docType}
										className="space-y-4 border rounded-lg p-4"
									>
										<h3 className="font-medium text-sm">
											{typeConfig.title || docType}
										</h3>

										<div className="space-y-2">
											<label
												htmlFor={`template-select-${docType}`}
												className="text-sm font-medium"
											>
												{typeConfig.templateLabel || "Modèle"}
											</label>
											<Select
												value={documentConfigs[docType]?.templateId || ""}
												onValueChange={(value) => {
													setDocumentConfigs((prev) => ({
														...prev,
														[docType]: {
															...prev[docType],
															templateId: value,
														},
													}));
												}}
												disabled={
													isLoadingTemplates || validTemplates.length === 0
												}
											>
												<SelectTrigger id={`template-select-${docType}`}>
													<SelectValue
														placeholder={
															isLoadingTemplates
																? "Chargement..."
																: validTemplates.length === 0
																	? "Aucun modèle disponible"
																	: "Sélectionnez un modèle"
														}
													/>
												</SelectTrigger>
												<SelectContent>
													{validTemplates.map((template: DocumentTemplate) => (
														<SelectItem key={template.id} value={template.id}>
															{template.name} (v{template.version})
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>

										<div className="space-y-2">
											<label
												htmlFor="receipt-select"
												className="text-sm font-medium"
											>
												Quittance
											</label>
											<Select
												value={quittanceReceiptId}
												onValueChange={setQuittanceReceiptId}
												disabled={
													isLoadingReceipts || availableReceipts.length === 0
												}
											>
												<SelectTrigger id="receipt-select">
													<SelectValue
														placeholder={
															isLoadingReceipts
																? "Chargement..."
																: availableReceipts.length === 0
																	? "Aucune quittance disponible"
																	: "Sélectionnez une quittance"
														}
													/>
												</SelectTrigger>
												<SelectContent>
													{availableReceipts.map((receipt) => (
														<SelectItem key={receipt.id} value={receipt.id}>
															{receipt.label} ({receipt.subtitle})
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>

										{documentConfigs[docType]?.templateId &&
											quittanceReceiptId && (
												<fieldset className="space-y-2">
													<legend className="text-sm font-medium">
														Format du document
													</legend>
													<RadioGroup
														value={
															documentConfigs[docType]?.format ||
															DocumentFormat.PDF
														}
														onValueChange={(value) => {
															setDocumentConfigs((prev) => ({
																...prev,
																[docType]: {
																	...prev[docType],
																	format: value as DocumentFormatValues,
																},
															}));
														}}
														className="grid grid-cols-2 gap-3"
													>
														{formatOptions.map((option) => {
															const Icon = option.icon;
															return (
																<Label
																	key={`${docType}-${option.value}`}
																	htmlFor={`${docType}-${option.value}`}
																	className="relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-input p-4 shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
																>
																	<RadioGroupItem
																		id={`${docType}-${option.value}`}
																		value={option.value}
																		className="absolute top-2 right-2"
																	/>
																	<Icon className="h-8 w-8 mb-1" />
																	<span className="font-medium text-center">
																		{option.label}
																	</span>
																	<span className="text-xs text-muted-foreground text-center">
																		{option.description}
																	</span>
																</Label>
															);
														})}
													</RadioGroup>
												</fieldset>
											)}
									</div>
								);
							})}

					<div className="space-y-2">
						<label htmlFor="signatory-select" className="text-sm font-medium">
							Signataire Dune
						</label>
						<Select
							value={selectedSignatoryId}
							onValueChange={setSelectedSignatoryId}
							disabled={isLoadingSignatories || !duneSignatories?.length}
						>
							<SelectTrigger id="signatory-select">
								<SelectValue
									placeholder={
										isLoadingSignatories
											? "Chargement..."
											: !duneSignatories?.length
												? "Aucun signataire disponible"
												: "Sélectionnez un signataire"
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{duneSignatories?.map((signatory) => (
									<SelectItem
										key={signatory.id}
										value={signatory.id.toString()}
									>
										{signatory.firstname} {signatory.lastname}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Annuler
					</Button>
					<Button
						onClick={handleGenerate}
						disabled={
							!selectedSignatoryId || !hasAtLeastOneSelection || isGenerating
						}
						isLoading={isGenerating}
					>
						Générer les documents
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default GenerateGFADocumentsModal;
